import { getEvent } from 'client/api/booking-calendar-api'
import BaseEntityNew from 'client/components/core/BaseEntity/BaseEntityNew'
import ChildrenEntities from 'client/components/core/ChildrenEntities'
import EntityExampleWrapperNew from 'client/components/core/EntityExampleWrapper/EntityExampleWrapperNew'
import { useUpdateEntity } from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import useSettingsManagement, { selectors } from 'client/hooks/useManagement'
import { useManagement, usePage, usePayment } from 'client/store'
import managementSelectors from 'client/store/management/managementSelectors'
import pageSelectors from 'client/store/page/pageSelectors'
import paymentSelectors from 'client/store/payment/paymentSelectors'
import { EntityProps } from 'client/types'
import {
  CommonCalendarButtonUi,
  CommonCalendarButtonWrapper,
} from 'common/components/entities/Calendar/calendar-step/ui/common-calendar-button-ui'
import { EventTimeZoneTypeEnum } from 'common/components/entities/Calendar/constants'
import { useEvent } from 'common/components/entities/Calendar/hooks/use-event'
import { CommonBookingPaidCalendar } from 'common/components/entities/Calendar/paid-calendar'
import { DigitalProductResourcesTypeEnum } from 'common/types/entities/ProductInterface'
import { BookingPaidCalendarInterface } from 'common/types/entities/booking-paid-calendar-interface'
import { getLocalTimeZone } from 'common/utils/date-utils'
import React, { useEffect } from 'react'
import { mockBooking } from '../BookingCalendar/mock-booking'
import { useTypography } from 'src/editor/shared/hooks/use-typography'

export const BookingPaidCalendar = ({
  entity,
  isMoveDownAllowed,
  isMoveUpAllowed,
}: EntityProps<BookingPaidCalendarInterface>) => {
  const activeStep = useSettingsManagement(selectors.getBookingCalendarStepType)

  const digitalProduct = usePayment(paymentSelectors.getDigitalProduct)
  const userTimeZone = useManagement(managementSelectors.getUserTimezone)
  const { isMobile } = useDeviceModeContext()
  const { event, isFetching } = useEvent({
    eventId: Number(
      digitalProduct?.resources?.find(
        ({ type }: { type: string }) =>
          type === DigitalProductResourcesTypeEnum.BookingEvent,
      )?.value,
    ),
    fetcher: getEvent,
  })

  const {
    calculatedFontSize,
    calculatedFontFamily,
    calculatedFontWeight,
    calculatedFontStyle,
  } = useTypography()

  const selectedTimeZone =
    event?.displayTimezoneType === EventTimeZoneTypeEnum.Local
      ? userTimeZone
      : event?.displayTimezone || getLocalTimeZone()

  const commonStyles = {
    color: entity.color,
    mobileColor: entity.mobileColor,
    mobilePadding: entity.mobilePadding,
    mobileMargin: entity.mobileMargin,
    fontFamily: calculatedFontFamily.desktop,
    fontSize: calculatedFontSize.desktop,
    mobileFontFamily: calculatedFontFamily.mobile,
    mobileFontSize: calculatedFontSize.mobile,
  }

  return (
    <BaseEntityNew
      entity={entity}
      isMoveUpAllowed={isMoveUpAllowed}
      isMoveDownAllowed={isMoveDownAllowed}
    >
      <EntityExampleWrapperNew
        visible={!event?.id}
        label="entities.paid_calendar.example_warning"
      >
        <CommonBookingPaidCalendar
          selectedDate={mockBooking.selectedDate}
          selectedTimeSlot={mockBooking.selectedTimeSlot}
          selectedLocationId={event?.locations?.[0]?.id}
          selectedTimeZone={selectedTimeZone ?? getLocalTimeZone()}
          availabilitySlots={mockBooking.availabilitySlots}
          event={event}
          fontSize={calculatedFontSize.desktop}
          mobileFontSize={calculatedFontSize.mobile}
          fontFamily={calculatedFontFamily.desktop}
          mobileFontFamily={calculatedFontFamily.mobile}
          activeStep={activeStep}
          entity={entity}
          isLoading={isFetching}
          formStepChildren={
            <ChildrenEntities
              id={entity.id}
              childIds={entity.childIds as never[]}
              type={entity.type}
            />
          }
          formStepButton={
            <CommonCalendarButtonWrapper>
              <CommonCalendarButtonUi
                backgroundColor={entity.selectedSlotBackgroundColor}
                {...commonStyles}
              >
                {entity.formButtonText}
              </CommonCalendarButtonUi>
            </CommonCalendarButtonWrapper>
          }
          isMobile={isMobile}
        />
      </EntityExampleWrapperNew>
    </BaseEntityNew>
  )
}
